import type { MouseEvent } from 'react';
import React from 'react';
import type {
  DeregisterValidationFn,
  FormBlurEventFn,
  FormChangeEventFn,
  RegisterValidationFn,
  SetFormValueFn,
} from '@noths/polaris-client-react-hooks';
import { CloseButton, TextButton } from '@noths/polaris-client-ribbons-design-system';
import { v4 as uuidv4 } from 'uuid';

import { TextInput } from 'src/components/atoms/TextInput/TextInput';
import { WeblinkInput } from 'src/components/atoms/WeblinkInput/WeblinkInput';
import {
  ADD_MORE_SOCIAL_MEDIA_COPY,
  ALREADY_EXISTS_ERROR,
  DELETE_SOCIAL_MEDIA_ARIA_LABEL,
  INSTAGRAM_COPY,
  OTHER_SOCIAL_MEDIA_COPY,
} from 'src/components/organisms/Signup/constants/copy';
import {
  ADD_OTHER_SOCIAL_MEDIA_INPUT_PREFIX_TESTID,
  DELETE_OTHER_SOCIAL_MEDIA_INPUT_PREFIX_TESTID,
} from 'src/components/organisms/Signup/constants/testIds';
import type { FormValidationErrors } from 'src/components/organisms/Signup/types';
import * as styles from 'src/styles/styles';
import type { SocialMediaAccount } from 'src/types/business';
import type { RegistrationForm } from 'src/types/registration';

interface Props {
  'data-position': string;
  'data-section': string;
  deregisterValidation: DeregisterValidationFn;
  errors: FormValidationErrors;
  formValues: RegistrationForm;
  onBlur: FormBlurEventFn;
  onFormChange: FormChangeEventFn;
  registerValidation: RegisterValidationFn<RegistrationForm>;
  setError: (errorName: string, errorMessage: string) => void;
  setFormValue: SetFormValueFn<RegistrationForm>;
}

export const SocialMedia = ({
  'data-position': dataPosition,
  'data-section': dataSection,
  deregisterValidation,
  errors,
  formValues,
  onBlur,
  onFormChange,
  registerValidation,
  setError,
  setFormValue,
}: Props) => {
  const TRACKING_POSITION_OFFSET = 2;

  const handleDeleteSocialMedia = (index: number) => {
    const updatedSocialMedia = formValues.socialMedia.filter((_, i) => i !== index);

    setFormValue('socialMedia', updatedSocialMedia);
    deregisterValidation(`socialMedia.${index}.url`);
  };

  const handleAddSocialmedia = (
    e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
    { id, url }: SocialMediaAccount,
  ) => {
    e.preventDefault();

    setFormValue('socialMedia', [...formValues.socialMedia, { id, url }]);
    registerValidation(`socialMedia.${formValues.socialMedia.length}.url`, {
      validate: {
        value: (urlVal, state) => state.socialMedia.some((sm) => sm.url === urlVal && sm.id !== id),
        message: ALREADY_EXISTS_ERROR,
      },
    });
  };
  return (
    <>
      <label css={styles.label} htmlFor="company-social-media-instagram">
        Your social media
      </label>
      <div css={styles.socialMediaInputs}>
        <TextInput
          data-position={`${dataPosition}.1`}
          data-section={dataSection}
          error={errors.instagramHandle as string | undefined}
          id="company-social-media-instagram"
          label={INSTAGRAM_COPY}
          name="instagramHandle"
          onChange={onFormChange}
          placeholder="Handle only"
          value={formValues.instagramHandle}
        />
        {formValues.socialMedia.map((socialMedia, index) => (
          <div css={styles.removableSocialMediaTextField} key={socialMedia.id}>
            <WeblinkInput
              data-position={`${dataPosition}.${index + TRACKING_POSITION_OFFSET}`}
              data-section={dataSection}
              data-testid={`${ADD_OTHER_SOCIAL_MEDIA_INPUT_PREFIX_TESTID}-${index}`}
              error={errors[`socialMedia.${index}.url`] as string | undefined}
              id={`company-social-media-${socialMedia.id}`}
              label={index === 0 ? OTHER_SOCIAL_MEDIA_COPY : ''}
              name={`socialMedia.${index}.url`}
              onBlur={onBlur}
              onChange={onFormChange}
              onWeblinkError={(errorMessage) => setError(`socialMedia.${index}.url`, errorMessage)}
              placeholder="Weblink"
              value={socialMedia.url}
            />
            <CloseButton
              aria-label={DELETE_SOCIAL_MEDIA_ARIA_LABEL}
              data-testid={`${DELETE_OTHER_SOCIAL_MEDIA_INPUT_PREFIX_TESTID}-${index}`}
              id={`delete-social-media-${socialMedia.id}`}
              onClick={() => handleDeleteSocialMedia(index)}
              size="small"
            />
          </div>
        ))}
      </div>
      <TextButton
        css={styles.addButton}
        id="company-social-media-add"
        onClick={(e) => {
          e.preventDefault();
          handleAddSocialmedia(e, { url: '', id: uuidv4() });
        }}
      >
        {ADD_MORE_SOCIAL_MEDIA_COPY}
      </TextButton>
    </>
  );
};
